import React, { Component } from 'react';
import { Alert } from 'react-bootstrap';
import vis from 'vis-network/dist/vis-network.min.js';
import 'vis-network/dist/dist/vis-network.min.css';

import Theme from 'context/theme';

import './graphCtrlVis.css';

/**
 * THE vis-network BRIDGE.
 *
 * vis draws the graph into a <canvas>. A canvas takes fill and stroke as strings passed to the 2D
 * context, and `ctx.fillStyle = 'var(--x)'` is neither an error nor a colour — the assignment is
 * silently ignored and the previous fill kept. It also does not repaint when a stylesheet changes.
 * So this bridge is two literal palettes plus `network.setOptions()` on a theme change, the same
 * shape mermaid's takes and for the same reason.
 *
 * ONE THING THE OPTIONS OBJECT GETS RIGHT BY ACCIDENT, worth writing down before somebody "fixes"
 * it: there is no `edges.color` key, so vis's default `inherit: "from"` applies and every edge is
 * drawn in its SOURCE NODE's border colour — #bbbbbb here, not the #848484 the docs quote. Which
 * means flipping `nodes.color.border` flips every edge with it, in both themes, for free. It is
 * deliberately left unset below so that stays true; setting any `edges.color` would turn inheritance
 * off and freeze every edge at one literal.
 *
 * TWO THINGS IT GETS WRONG, both found here and both fixed by the palette. `nodes.font.color` was
 * never set, so every node label was vis's own #343434 rather than the app's ink. And `edges.font`
 * was never set, so every edge LABEL is drawn with vis's default `strokeWidth: 2,
 * strokeColor: "#ffffff"` halo — verified in the bundle. That halo is invisible on a white canvas,
 * which is why it has never had to be in this file, and unmissable on a dark one.
 */

// The two palettes, literal for literal. LIGHT is what ships today, with the two omissions above
// filled in at the values vis was already effectively using or at the app's own ink; DARK is each
// token's dark literal. vis cannot read a variable, so these cannot be var() — see the header.
const VIS_PALETTE = {
  light: {
    nodeBg: '#ffffff',              // --bh-surface
    nodeBorder: '#bbbbbb',          // unchanged; every edge inherits this, see the header
    nodeHighlightBg: '#f1f8ff',     // unchanged - --bh-accent-tint-2
    nodeHighlightBorder: '#0366d6', // unchanged - --bh-accent-ink
    nodeInk: '#212529',             // --bh-text; was unset, i.e. vis's #343434
    edgeLabelInk: '#212529',        // --bh-text; was unset, same default
    edgeLabelHalo: '#ffffff',       // --bh-canvas, and identical to vis's own default
    shadow: '#eeeeee',              // unchanged
    recentBg: '#e8e8e8'             // unchanged; the "changed in the last diff" highlight
  },
  dark: {
    nodeBg: '#202024',              // --bh-surface: a node is a card on the page
    nodeBorder: '#45454d',          // --bh-border-strong, and therefore the edge colour too
    nodeHighlightBg: '#35353c',     // --bh-fill-strong: one rung up, the selection lift
    nodeHighlightBorder: '#93bdf8', // --bh-accent
    nodeInk: '#e4e4e7',             // --bh-text
    edgeLabelInk: '#e4e4e7',        // --bh-text
    edgeLabelHalo: '#171719',       // --bh-canvas: the halo IS the ground, so it flips with it
    shadow: 'rgba(0, 0, 0, 0.55)',  // #eeeeee would be a white bloom around every node
    recentBg: '#35353c'             // --bh-fill-strong
  }
};

function visOptions(palette) {
  return {
    physics: {
      enabled: true,
      stabilization: {
        enabled: true,
        fit: true
      }
    },
    layout: {
      randomSeed: 2
    },
    nodes: {
      shape: "box",
      chosen: {
        label: false
      },
      shapeProperties: {
        borderRadius: 2
      },
      font: {
        multi: true,
        face: "Roboto",
        size: 12,
        align: "left",
        color: palette.nodeInk
      },
      margin: {
        top: 5,
        bottom: 5,
        left: 8,
        right: 8
      },
      color: {
        background: palette.nodeBg,
        border: palette.nodeBorder,
        highlight: {
          background: palette.nodeHighlightBg,
          border: palette.nodeHighlightBorder
        }
      },
      shadow: {
        enabled: true,
        color: palette.shadow,
        x: 2,
        y: 2,
        size: 1
      },
      opacity: 0.8
    },
    edges: {
      // No `color` key on purpose — see the header.
      font: {
        size: 12,
        face: "Roboto",
        color: palette.edgeLabelInk,
        strokeColor: palette.edgeLabelHalo
      },
      shadow: {
        enabled: false
      },
      arrows: {
        to: {
          enabled: true,
          scaleFactor: 0.5
        }
      }
    }
  };
}

// Exported purely for unit testing: `visOptions` is where a palette key can silently stop being
// read, and VIS_PALETTE is where one half can grow an entry the other lacks. Neither is reachable
// from the component's public surface.
export { VIS_PALETTE, visOptions };

export default class GraphControl extends Component {
  constructor(props) {
    super(props);

    this.uiVertexIndex = new Map();
    this.network = null;
    this.nodes = new vis.DataSet([]);
    this.edges = new vis.DataSet([]);

    this.props.shell.graphCtrlInterface = {
      incorporateDiff: this.incorporateDiff,
      refreshAppearance: this.refreshAppearance,
      activate: this.activate,
      clear: this.clear
    };
  }

  /**
   * Control interface
   */

  activate = () => {
    this.adjustUiToRecentUpdates();
  }

  incorporateDiff = () => {
    this.incorporateDiffCore();
    this.adjustUiToRecentUpdates();
  }

  refreshAppearance = () => {
    this.refreshAppearanceCore();
    this.adjustUiToRecentUpdates();
  }

  clear = () => {
    this.edges.clear();
    this.nodes.clear();
    this.adjustUiToRecentUpdates();
  }

  /**
   * Callbacks
   */

  onSelectContent = (params) => {
    for (var nodeId of params.nodes) {
      this.applyNodeSelection(nodeId);
      return;
    }

    for (var edgeId of params.edges) {
      this.applyEdgeSelection(edgeId);
      return;
    }
  }

  applyNodeSelection(nodeId) {
    let uiVertex = this.nodes.get(nodeId);
    let vertex = uiVertex.vertex;
    this.props.shell.shellInterface.updateDetails(true, "vertex", vertex);
  }

  applyEdgeSelection(edgeId) {
    let uiEdge = this.edges.get(edgeId);
    let edge = uiEdge.edge;
    this.props.shell.shellInterface.updateDetails(true, "edge", edge);
  }

  /**
   * Rendering
   */

  createGraph() {
    // create a network
    var container = document.getElementById("explore-preview-graph");
    var data = {
      nodes: this.nodes,
      edges: this.edges
    };
    this.network = new vis.Network(container, data, visOptions(VIS_PALETTE[Theme.resolved()]));
    this.applyPalette(Theme.resolved());

    // A theme change repaints the canvas: new options for the shared node and edge styling, and a
    // pass over the DataSet for the per-node colours the diff highlighting writes directly into it.
    this.unsubscribeTheme = Theme.subscribe(() => {
      if ( !this.network ) {
        return;
      }
      const theme = Theme.resolved();
      this.network.setOptions(visOptions(VIS_PALETTE[theme]));
      this.applyPalette(theme);
      this.network.redraw();
    });

    // Setup callbacks
    this.network.on("select", this.onSelectContent);
  }

  /**
   * The two per-node colour styles the diff highlighting assigns are written into the DataSet, not
   * into options, so `setOptions` alone cannot reach a node that is already on screen — it would
   * keep its old background while everything around it flipped. Re-stamp them, preserving which
   * nodes are "recent" by comparing against the palette that was in force a moment ago.
   */
  applyPalette(theme) {
    const palette = VIS_PALETTE[theme];
    const wasRecent = this.recentNodeColorStyle;
    this.normalNodeColorStyle = { background: palette.nodeBg };
    this.recentNodeColorStyle = { background: palette.recentBg };
    if ( !wasRecent ) {
      return;                                   // first call: nothing is in the DataSet yet
    }
    const updates = [];
    for ( const nodeId of this.nodes.getIds() ) {
      const current = this.nodes.get(nodeId).color;
      const isRecent = current && current.background === wasRecent.background;
      updates.push({
        id: nodeId,
        color: isRecent ? this.recentNodeColorStyle : this.normalNodeColorStyle
      });
    }
    this.nodes.update(updates);
  }

  incorporateDiffCore() {
    // Clear previous node highlighting
    var notesToUpdate = [];
    for (var nodeId of this.nodes.getIds()) {
      notesToUpdate.push({
        id: nodeId,
        color: this.normalNodeColorStyle
      });
    }
    this.nodes.update(notesToUpdate);

    // Clear previous edge highlighting
    var edgesToUpdate = [];
    for (var edgeId of this.edges.getIds()) {
      edgesToUpdate.push({
        id: edgeId,
        width: 1
      });
    }
    this.edges.update(edgesToUpdate);

    if ( !this.props.shell.graph.mostRecentDiff ) {
      return;
    }

    // Process new nodes
    for (var nodeId of this.props.shell.graph.mostRecentDiff.nodes()) {
      // Skip nodes that are already present
      if ( this.nodes.get(nodeId) ) {
        // Highlight the node
        this.nodes.update([{
          id: nodeId,
          color: this.recentNodeColorStyle
        }]);
        continue;
      }

      // Add node
      const node = this.props.shell.graph.mostRecentDiff.node(nodeId);
      const LabelTemplate = this.findAppearanceTemplate("vertex", node);
      const nodeLabel = this.renderTemplate(LabelTemplate, {vertex: node});
      const uiVertex = {
        id: nodeId,
        label: nodeLabel,
        color: this.recentNodeColorStyle,
        vertex: node
      }
      this.nodes.add(uiVertex);
      this.uiVertexIndex.set(nodeId, uiVertex);
    }

    // Process new edges
    for (var edgeObj of this.props.shell.graph.mostRecentDiff.edges()) {
      // Skip edges that are already present
      const edge = this.props.shell.graph.mostRecentDiff.edge(edgeObj);
      if ( this.edges.get(edge.id) ) {
        // Highlight the edge
        this.edges.update([{
          id: edge.id,
          width: 2
        }]);
        continue;
      }

      // Add edge
      const edgeTemplate = this.findAppearanceTemplate("edge", edge)
      const edgeLabel = this.renderTemplate(edgeTemplate, {edge: edge});
      const uiEdge = {
        id: edge.id,
        from: edgeObj.v,
        to: edgeObj.w,
        width: 2,
        label: edgeLabel,
        edge: edge
      };
      this.edges.add(uiEdge);
    }
  }

  refreshAppearanceCore() {
    // Update nodes
    var notesToUpdate = [];
    for (var nodeId of this.nodes.getIds()) {
      const node = this.nodes.get(nodeId).vertex;
      const LabelTemplate = this.findAppearanceTemplate("vertex", node);
      const nodeLabel = this.renderTemplate(LabelTemplate, {vertex: node});
      notesToUpdate.push({
        id: nodeId,
        label: nodeLabel
      });
    }
    this.nodes.update(notesToUpdate);

    // Update edges
    var edgesToUpdate = [];
    for (var edgeId of this.edges.getIds()) {
      const edge = this.edges.get(edgeId).edge;
      const edgeTemplate = this.findAppearanceTemplate("edge", edge)
      const edgeLabel = this.renderTemplate(edgeTemplate, {edge: edge});
      edgesToUpdate.push({
        id: edgeId,
        label: edgeLabel
      });
    }
    this.edges.update(edgesToUpdate);
  }

  adjustUiToRecentUpdates() {
    this.forceUpdate();
    this.network.fit();
  }

  /**
   * Appearance
   */

  findAppearanceTemplate(type, object) {
    // Default templates
    if ( "vertex" == type ) {
      var template = "${vertex.class} (${vertex.id})";
    } else if ( "edge" == type ) {
      var template = "${edge.class}";
    } else {
      var template = "unknown";
    }

    // Templates from appearance store
    if ( this.props.shell.appearance ) {
      var appearanceItem = null
      if ( "vertex" == type ) {
        appearanceItem = this.props.shell.appearance.verticesIndexByClass[object["class"]];
      } else if ( "edge" == type ) {
        appearanceItem = this.props.shell.appearance.edgesIndexByClass[object["class"]];
      }
      if ( appearanceItem ) {
        template = appearanceItem["label"]["template"]
      }
    }

    return template;
  }

  renderTemplate(template, data) {
    let inject = (str, obj) => str.replace(/\${(.*?)}/g, (x, g) => {
      try {
        let value = obj;
        for ( let segment of g.split(".") ) {
          value = value[segment];
        }
        if ( undefined === value ) {
          throw "undefined";
        }
        return value;
      } catch (err) {
        return "<?>";
      }
    });
    return inject(template, data);
  }

  /**
   * Component
   */

  componentDidMount() {
    this.createGraph();
  }

  componentWillUnmount() {
    if ( this.unsubscribeTheme ) {
      this.unsubscribeTheme();
    }
    if ( this.network ) {
      this.network.destroy();
      this.network = null;
    }
  }

  render() {
    // Presence of nodes is deemed sufficient for rendering
    const hasContent = this.nodes.length > 0;

    return (
      <>
        <div id="explore-preview-graph" className={hasContent ? "explore-preview-graph" : "hidden"} />

        <div className={hasContent ? "hidden" : ""}>
          <Alert variant="secondary">
            No content available to render. <br/>
            <br/>
            Run query to fetch some data for visualization. <br/>
            <br/>
            Start with something as simple as <br/>
            <code>SELECT FROM E LIMIT 25</code> <br/>
            to get a sense for what is available.
          </Alert>
        </div>
      </>
    );
  }
}
